/**
 * Persian explanatory UI copy (design spec §14.6 item 10). Every explanatory
 * text shown in the partner and admin panels lives here so the tone stays
 * consistent and editable in one place.
 */

export const COMMISSION_TIMELINE_STEPS = [
  'ordered',
  'paid',
  'in_guarantee',
  'approved',
  'available',
  'paid_out',
  'held',
  'reversed',
  'void',
] as const;

export type CommissionTimelineStep = (typeof COMMISSION_TIMELINE_STEPS)[number];

export interface CopyItem {
  label: string;
  explanation: string;
}

/** Commission timeline steps. The first six are the normal path; the last three are side exits. */
export const COMMISSION_TIMELINE_FA: Readonly<Record<CommissionTimelineStep, CopyItem>> = Object.freeze({
  ordered: {
    label: 'سفارش ثبت شد',
    explanation: 'مشتری سفارش را ثبت کرده است و منتظر پرداخت فاکتور هستیم.',
  },
  paid: {
    label: 'مشتری پرداخت کرد',
    explanation: 'فاکتور پرداخت شد و پورسانت شما بر اساس مبلغ پرداختی محاسبه شد.',
  },
  in_guarantee: {
    label: 'در دورهٔ ضمانت',
    explanation: 'مشتری در این مدت می‌تواند انصراف بدهد؛ پس از پایان آن، پورسانت قطعی می‌شود.',
  },
  approved: {
    label: 'تأیید شد',
    explanation: 'دورهٔ ضمانت تمام شد و سرویس مشتری فعال است، پس پورسانت قطعی شد.',
  },
  available: {
    label: 'قابل برداشت',
    explanation: 'این مبلغ به موجودی قابل برداشت شما اضافه شد و می‌توانید آن را به کیف پول یا شبا ببرید.',
  },
  paid_out: {
    label: 'به شما پرداخت شد',
    explanation: 'مبلغ به کیف پول یا حساب بانکی شما واریز شد و رسید آن در بخش تسویه‌ها آماده است.',
  },
  held: {
    label: 'در حال بررسی',
    explanation: 'این پورسانت برای بررسی بیشتر نگه داشته شده است و پس از تصمیم تیم میزبان‌فا ادامه پیدا می‌کند.',
  },
  reversed: {
    label: 'برگشت خورد',
    explanation: 'مشتری مبلغ را پس گرفت و پورسانت به همان نسبت برگشت خورد.',
  },
  void: {
    label: 'بی‌اثر شد',
    explanation: 'این سفارش شرایط پورسانت را نداشت؛ دلیل آن را کنار همین مورد می‌بینید.',
  },
});

export interface GlossaryEntry {
  term: string;
  definition: string;
}

/** Glossary and tooltip texts. */
export const GLOSSARY_FA: readonly GlossaryEntry[] = Object.freeze([
  {
    term: 'سرویس فعال',
    definition: 'سرویسی که از طریق شما خریده شده، دورهٔ ضمانت ۱۴ روزه‌اش تمام شده و الان تعلیق یا لغو نشده است. سرویس‌هایی که هنوز در دورهٔ ضمانت‌اند جدا نشان داده می‌شوند و بعد از ضمانت به سرویس‌های فعال اضافه می‌شوند. در شمردن سطح، چند سرویسِ یک مشتری یک سرویس حساب می‌شوند.',
  },
  {
    term: 'نرخ تمدید',
    definition: 'درصدی که هر بار مشتری سرویسی را که از طریق شما خریده تمدید کند، به شما می‌رسد.',
  },
  {
    term: 'سهم کد',
    definition: 'درصد پورسانت فروشی که با کد تخفیف شما آمده است. تخفیف مشتری را میزبان‌فا می‌دهد و این درصد از مبلغی حساب می‌شود که مشتری پس از تخفیف پرداخت کرده.',
  },
  {
    term: 'کد معرف',
    definition: 'کد کوتاه مخصوص شما که مشتری هنگام خرید در فیلد «کد معرف» وارد می‌کند. تخفیفی به مشتری نمی‌دهد ولی سفارش را به نام شما ثبت می‌کند.',
  },
  {
    term: 'کد تخفیف بازاریاب',
    definition: 'کدی که خودتان می‌سازید و با آن به مشتری تخفیف می‌دهید: یک کد ثابت، یا چند کد محدود با تخفیف بیشتر. تخفیف را میزبان‌فا می‌دهد و درصد پورسانت شما عوض نمی‌شود.',
  },
  {
    term: 'دورهٔ ضمانت',
    definition: 'مدتی پس از پرداخت که مشتری می‌تواند انصراف بدهد. پورسانت تا پایان این دوره قطعی نمی‌شود.',
  },
  {
    term: 'تسویه',
    definition: 'انتقال موجودی قابل برداشت شما به کیف پول میزبان‌فا یا حساب بانکی با شمارهٔ شبا.',
  },
  {
    term: 'کیف پول',
    definition: 'اعتبار حساب کاربری شما در میزبان‌فا که برای خرید و تمدید سرویس‌ها استفاده می‌شود. تسویه به کیف پول ۵٪ پاداش دارد ولی به حساب بانکی برنمی‌گردد.',
  },
  {
    term: 'سطح',
    definition: 'رتبهٔ شما در برنامه که نرخ پورسانت را تعیین می‌کند. با افزایش سرویس‌های فعال یا فروش‌های تأییدشده، سطح شما بالا می‌رود.',
  },
  {
    term: 'شریک راهبردی',
    definition: 'بازاریابی که با میزبان‌فا قرارداد جداگانه دارد و نرخ پورسانتش طبق همان قرارداد تعیین می‌شود، نه بر اساس سطح.',
  },
  {
    term: 'سفیر',
    definition: 'فرد شناخته‌شده‌ای که با قرارداد تبلیغاتی، بازاریاب تازه به برنامه معرفی می‌کند و برای هر بازاریاب فعال‌شده پاداش ثابت می‌گیرد.',
  },
]);

export interface HowItWorksStep {
  title: string;
  description: string;
}

/** The four-step "how it works" flow: لینک یا کد ← خرید مشتری ← ۱۴ روز ضمانت ← کیف پول یا شبا. */
export const HOW_IT_WORKS_FA: readonly HowItWorksStep[] = Object.freeze([
  {
    title: 'لینک یا کد',
    description: 'لینک اختصاصی، کد معرف یا کد تخفیف خودتان را با مخاطبانتان به اشتراک بگذارید.',
  },
  {
    title: 'خرید مشتری',
    description: 'مشتری با لینک یا کد شما خرید می‌کند و سفارش به نام شما ثبت می‌شود.',
  },
  {
    title: '۱۴ روز ضمانت',
    description: 'پورسانت ۱۴ روز در دورهٔ ضمانت می‌ماند تا اگر مشتری انصراف داد، حساب‌ها درست بماند.',
  },
  {
    title: 'کیف پول یا شبا',
    description: 'پس از تأیید، مبلغ قابل برداشت است و می‌توانید آن را به کیف پول یا حساب بانکی خود منتقل کنید.',
  },
]);
